// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type { StorefrontCatalog } from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Purchasable products and USD prices: the single-story software package, the Unlimited plan, credit packs and printed book formats.
 * @summary Storefront product catalog
 */
export const storefrontCatalog = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StorefrontCatalog>(
    { url: `/api/storefront/catalog/`, method: "GET", signal },
    options
  );
};

export const getStorefrontCatalogQueryKey = () => {
  return [`/api/storefront/catalog/`] as const;
};

export const getStorefrontCatalogQueryOptions = <
  TData = Awaited<ReturnType<typeof storefrontCatalog>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof storefrontCatalog>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStorefrontCatalogQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof storefrontCatalog>>
  > = ({ signal }) => storefrontCatalog(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof storefrontCatalog>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type StorefrontCatalogQueryResult = NonNullable<
  Awaited<ReturnType<typeof storefrontCatalog>>
>;
export type StorefrontCatalogQueryError = unknown;

export function useStorefrontCatalog<
  TData = Awaited<ReturnType<typeof storefrontCatalog>>,
  TError = unknown
>(options: {
  query: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof storefrontCatalog>>,
      TError,
      TData
    >
  > &
    Pick<
      DefinedInitialDataOptions<
        Awaited<ReturnType<typeof storefrontCatalog>>,
        TError,
        Awaited<ReturnType<typeof storefrontCatalog>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStorefrontCatalog<
  TData = Awaited<ReturnType<typeof storefrontCatalog>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof storefrontCatalog>>,
      TError,
      TData
    >
  > &
    Pick<
      UndefinedInitialDataOptions<
        Awaited<ReturnType<typeof storefrontCatalog>>,
        TError,
        Awaited<ReturnType<typeof storefrontCatalog>>
      >,
      "initialData"
    >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useStorefrontCatalog<
  TData = Awaited<ReturnType<typeof storefrontCatalog>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof storefrontCatalog>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Storefront product catalog
 */

export function useStorefrontCatalog<
  TData = Awaited<ReturnType<typeof storefrontCatalog>>,
  TError = unknown
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof storefrontCatalog>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getStorefrontCatalogQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
